import React, { useState, useEffect, useRef } from 'react';
import { MultiSelect } from 'react-multi-select-component';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import Pagination from '../../components/Pagination.tsx';
import LoaderOverlay from '../../components/LoaderOverlay';
import layoutStyles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import reportStyles from '../../styles/ReportesIngresosDiarios.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';

import { getIngresosDiarios, getCajeros } from '../../api/reportesFinancieros';
import { generateReporteIngresosPDF } from '../../utils/generateReporteIngresosPDF';

const ReportesIngresosDiariosScreen = () => {
  // Conceptos únicos (cnc)
  const [selectedConceptos, setSelectedConceptos] = useState([]);
  const [ingresos, setIngresos] = useState([]);
  const conceptosUnicos = React.useMemo(() => {
    const conceptosSet = new Set(ingresos.map(row => row.cnc).filter(Boolean));
    return Array.from(conceptosSet).sort().map(concepto => ({ value: concepto, label: concepto }));
  }, [ingresos]);

  // Seleccionar "Todos" por defecto cuando cambian los conceptos disponibles
  // Ya no seleccionar ningún concepto por defecto
  useEffect(() => {
    // Si los conceptos cambian y no hay selección, no seleccionar nada
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptosUnicos]);
  // Cajeros
  const [cajeros, setCajeros] = useState([]);
  const [selectedCajeros, setSelectedCajeros] = useState([]);
  const cajerosOptions = React.useMemo(() => {
    return cajeros.map(c => ({ value: String(c.id), label: `${c.nombre} (${c.usuario})` }));
  }, [cajeros]);

  // Forma de pago
  const [selectedFormasPago, setSelectedFormasPago] = useState([]);
  const formasPagoInitialized = useRef(false);
  const formasPagoUnicas = React.useMemo(() => {
    const set = new Set(ingresos.map(row => row.formaPago).filter(Boolean));
    return Array.from(set).sort().map(fp => ({ value: fp, label: fp }));
  }, [ingresos]);

  // Seleccionar todas las formas de pago excepto PAGO_EN_LINEA al cargar por primera vez
  useEffect(() => {
    if (!formasPagoInitialized.current && formasPagoUnicas.length > 0) {
      formasPagoInitialized.current = true;
      setSelectedFormasPago(formasPagoUnicas.filter(fp => fp.value !== 'PAGO_EN_LINEA'));
    }
  }, [formasPagoUnicas]);

  // Cargar cajeros al montar
  useEffect(() => {
    getCajeros().then(setCajeros).catch(() => setCajeros([]));
  }, []);
    // Campus seleccionado
    const [selectedCampus, setSelectedCampus] = useState(() => localStorage.getItem('selectedCampus') || '');

    // Escuchar cambios de campus desde HeaderGlobal
    useEffect(() => {
      const handler = (e) => {
        const campus = (e.detail && e.detail.campus) || localStorage.getItem('selectedCampus') || '';
        setSelectedCampus(campus);
      };
      window.addEventListener('campusChanged', handler);
      // Inicializar
      setSelectedCampus(localStorage.getItem('selectedCampus') || '');
      return () => window.removeEventListener('campusChanged', handler);
    }, []);
  const { isCollapsed } = useSidebar();

  // ...eliminado, ya está arriba...
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('APLICADO');
  // Inicializar fecha de hoy en horario local de México
  const getTodayLocal = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  };
  const [dateRange, setDateRange] = useState([getTodayLocal(), null]);
  const [startDate, endDate] = dateRange;

  // Paginación para la tabla principal
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const formatDisplayDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const DateRangeInput = React.forwardRef(({ value, onClick, start, end }, ref) => {
    let text = '';
    if (start && end) {
      text = `${formatDisplayDate(start)} - ${formatDisplayDate(end)}`;
    } else if (start) {
      text = formatDisplayDate(start);
    }

    return (
      <input
        ref={ref}
        onClick={onClick}
        readOnly
        className={estudiantesStyles.searchInput}
        placeholder="Selecciona fecha o rango"
        value={text}
        style={{
          textAlign: start && !end ? 'center' : 'left',
          cursor: 'pointer',
        }}
      />
    );
  });

  DateRangeInput.displayName = 'DateRangeInput';

  const parseFecha = (value) => {
    if (!value) return null;
    const [dia, mes, anio] = value.split('/');
    // Crear fecha en horario local (a medianoche) para evitar desfases por zona horaria
    return new Date(parseInt(anio, 10), parseInt(mes, 10) - 1, parseInt(dia, 10));
  };

  // Normalizar fechas de filtro a solo fecha (sin hora)
  const normalizedStartDate = startDate
    ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
    : null;
  const normalizedEndDate = endDate
    ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
    : null;

  const matriculaEspecialCajeroPattern = /\s*\(\d+\)\s*$/;

  const filteredIngresos = ingresos.filter((row) => {
    // Filtrar por conceptos seleccionados (cnc)
    const selectedValues = selectedConceptos.map(opt => opt.value);
    if (
      selectedValues.length > 0 &&
      !selectedValues.includes('__ALL__') &&
      !selectedValues.includes(row.cnc)
    ) {
      return false;
    }
    // Filtrar por estatus de pago
    if (selectedStatus && selectedStatus !== 'TODOS') {
      const estado = (row.estado_pago || '').toUpperCase();
      if (estado !== selectedStatus.toUpperCase()) return false;
    }

    // Filtrar por campus seleccionado
    if (selectedCampus && selectedCampus !== 'Todos') {
      if ((row.campus || '').trim() !== selectedCampus.trim()) return false;
    }

    // Filtrar por cajero seleccionado
    const selectedCajeroValues = selectedCajeros.map(opt => opt.value);
    if (selectedCajeroValues.length > 0 && !selectedCajeroValues.includes(String(row.cajero_id))) {
      return false;
    }
    if (selectedCajeroValues.length > 0 && matriculaEspecialCajeroPattern.test(String(row.matricula || ''))) {
      return false;
    }

    // Filtrar por forma de pago
    const selectedFormasPagoValues = selectedFormasPago.map(opt => opt.value);
    if (selectedFormasPagoValues.length > 0 && !selectedFormasPagoValues.includes(row.formaPago || '')) {
      return false;
    }

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const inNombre = row.nombre.toLowerCase().includes(term);
      const inMatricula = row.matricula.toLowerCase().includes(term);
      const inFolio = row.folio.toLowerCase().includes(term);
      const inCnc = (row.cnc || '').toLowerCase().includes(term); // Concepto de Cobro
      const inPeriodo = (row.periodo || '').toLowerCase().includes(term);
      const inCobranza = (row.cobranza || '').toLowerCase().includes(term);
      if (!inNombre && !inMatricula && !inFolio && !inCnc && !inPeriodo && !inCobranza) return false;
    }

    if (normalizedStartDate || normalizedEndDate) {
      const fechaRow = parseFecha(row.fecha);
      if (!fechaRow) return false;
      // Comparar solo día, mes y año
      const rowDay = fechaRow.getDate();
      const rowMonth = fechaRow.getMonth();
      const rowYear = fechaRow.getFullYear();
      if (normalizedStartDate) {
        const startDay = normalizedStartDate.getDate();
        const startMonth = normalizedStartDate.getMonth();
        const startYear = normalizedStartDate.getFullYear();
        const rowDateNum = rowYear * 10000 + rowMonth * 100 + rowDay;
        const startDateNum = startYear * 10000 + startMonth * 100 + startDay;
        if (rowDateNum < startDateNum) {
          return false;
        }
      }
      if (normalizedEndDate) {
        const endDay = normalizedEndDate.getDate();
        const endMonth = normalizedEndDate.getMonth();
        const endYear = normalizedEndDate.getFullYear();
        const rowDateNum = rowYear * 10000 + rowMonth * 100 + rowDay;
        const endDateNum = endYear * 10000 + endMonth * 100 + endDay;
        if (rowDateNum > endDateNum) {
          return false;
        }
      }
    }

    return true;
  });

  const getSortableValue = (row, key) => {
    switch (key) {
      case 'no':
        return Number(row.no ?? 0) || 0;
      case 'fecha': {
        const date = parseFecha(row.fecha);
        return date ? date.getTime() : 0;
      }
      case 'matricula':
        return String(row.matricula || '').trim().toLowerCase();
      case 'nombre':
        return String(row.nombre || '').trim().toLowerCase();
      case 'campus':
        return String(row.campus || '').trim().toLowerCase();
      case 'grupo':
        return String(row.grupo || '').trim().toLowerCase();
      case 'periodoCobranza':
        return `${String(row.periodo || '').trim().toLowerCase()}|${String(row.cobranza || '').trim().toLowerCase()}`;
      case 'cnc':
        return String(row.cnc || '').trim().toLowerCase();
      case 'formaPago':
        return String(row.formaPago || '').trim().toLowerCase();
      case 'estado_pago':
        return String(row.estado_pago || '').trim().toLowerCase();
      case 'folio':
        return String(row.folio || '').trim().toLowerCase();
      case 'observaciones':
        return String(row.observaciones || '').trim().toLowerCase();
      case 'cajero':
        return String(row.cajero || '').trim().toLowerCase();
      case 'cargo':
        return Number(row.cargo ?? 0) || 0;
      case 'recargo':
        return Number(row.recargo ?? 0) || 0;
      case 'comision':
        return getComisionRow(row);
      case 'importe':
        return getImporteConComisionRow(row);
      default:
        return '';
    }
  };

  const sortedIngresos = React.useMemo(() => {
    if (!sortConfig.key) return filteredIngresos;

    return [...filteredIngresos].sort((a, b) => {
      const valueA = getSortableValue(a, sortConfig.key);
      const valueB = getSortableValue(b, sortConfig.key);

      if (valueA < valueB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredIngresos, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        key,
        direction: 'asc',
      };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span style={{ marginLeft: 6, opacity: 0.35 }}>↕</span>;
    }

    return sortConfig.direction === 'asc'
      ? <span style={{ marginLeft: 6 }}>↑</span>
      : <span style={{ marginLeft: 6 }}>↓</span>;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [start, end] = dateRange;
        const fechaInicio = start ? start.toISOString().split('T')[0] : undefined;
        const fechaFin = end ? end.toISOString().split('T')[0] : fechaInicio;

        const response = await getIngresosDiarios({
          fechaInicio,
          fechaFin,
          campusId: null,
          estadoPago: selectedStatus === 'TODOS' ? undefined : selectedStatus,
        });

        setIngresos(response?.results || []);
      } catch (err) {
        console.error('Error al cargar ingresos diarios:', err);
        setError('Error al cargar el reporte de ingresos diarios');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange, getIngresosDiarios, selectedStatus]);

  // Resetear a la primera página cuando cambian los filtros
  useEffect(() => {
    setPage(1);
  }, [searchTerm, startDate, endDate, selectedCajeros, selectedStatus]);

  const totalPages = Math.max(1, Math.ceil(sortedIngresos.length / pageSize));
  const paginatedIngresos = sortedIngresos.slice((page - 1) * pageSize, page * pageSize);

  const getComisionRow = (row) => {
    const cargo = Number(row?.cargo) || 0;
    const recargo = Number(row?.recargo) || 0;
    return row?.formaPago === 'PAGO_EN_LINEA' ? (cargo + recargo) * 0.02 : 0;
  };

  const getImporteConComisionRow = (row) => {
    const importe = Number(row?.importe) || 0;
    return importe + getComisionRow(row);
  };

  const ingresoTotalDia = filteredIngresos.reduce((acc, row) => acc + getImporteConComisionRow(row), 0);

  const totalComision = filteredIngresos.reduce((acc, row) => acc + getComisionRow(row), 0);
  const totalCargo = filteredIngresos.reduce((acc, row) => acc + (Number(row?.cargo) || 0), 0);
  const totalRecargo = filteredIngresos.reduce((acc, row) => acc + (Number(row?.recargo) || 0), 0);

  const totalesPorSeccion = Object.values(
    filteredIngresos.reduce((acc, row) => {
      const key = row.seccion || 'N/A';
      if (!acc[key]) {
        acc[key] = { seccion: key, cargo: 0, recargo: 0, comision: 0, total: 0 };
      }
      acc[key].cargo += Number(row.cargo) || 0;
      acc[key].recargo += Number(row.recargo) || 0;
      acc[key].comision += Number(getComisionRow(row)) || 0;
      acc[key].total += getImporteConComisionRow(row);
      return acc;
    }, {})
  );


  const totalesPorSerie = Object.values(
    filteredIngresos.reduce((acc, row) => {
      const key = row.serie || 'N/A';
      if (!acc[key]) {
        acc[key] = { serie: key, cargo: 0, recargo: 0, comision: 0, total: 0 };
      }
      acc[key].cargo += Number(row.cargo) || 0;
      acc[key].recargo += Number(row.recargo) || 0;
      acc[key].comision += Number(getComisionRow(row)) || 0;
      acc[key].total += getImporteConComisionRow(row);
      return acc;
    }, {})
  );

  // Totales por concepto (agrupando por CNC)
  const totalesPorConcepto = Object.values(
    filteredIngresos.reduce((acc, row) => {
      const key = row.cnc || 'N/A';
      if (!acc[key]) {
        acc[key] = { concepto: key, nombre_concepto: row.nombre_concepto || '', cargo: 0, recargo: 0, comision: 0, total: 0 };
      }
      // Si el nombre_concepto está vacío y este row lo tiene, lo actualiza
      if (!acc[key].nombre_concepto && row.nombre_concepto) {
        acc[key].nombre_concepto = row.nombre_concepto;
      }
      acc[key].cargo += Number(row.cargo) || 0;
      acc[key].recargo += Number(row.recargo) || 0;
      acc[key].comision += Number(getComisionRow(row)) || 0;
      acc[key].total += getImporteConComisionRow(row);
      return acc;
    }, {})
  );

  const formatMoney = (value) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
    }).format(value);

  const handleDownloadExcel = () => {
    const wb = XLSX.utils.book_new();

    // Encabezados generales (mismo texto que en la pantalla)
    const fechaRangoTexto = startDate || endDate
      ? startDate && endDate
        ? `${formatDisplayDate(startDate)} al ${formatDisplayDate(endDate)}`
        : formatDisplayDate(startDate || endDate)
      : '';

    const sheetData = [];

    // Encabezado (se añade un ligero margen con espacios no separables para compensar que Excel ignore la alineación)
    const pad = '\u00A0\u00A0\u00A0\u00A0\u00A0';
    sheetData.push([`${pad}Centro De Estudios Superiores CMP, A.C.`]);
    sheetData.push([
      `${pad}Ave. Venustiano Carranza Nte. No.Ext. 614 CENTRO CP 64000 MONTERREY, NUEVO LEÓN`,
    ]);
    sheetData.push([`${pad}Reporte de ingresos`]);
    sheetData.push([]);

    // Detalle

    const detalleHeader = [
      'No.',
      'Fecha',
      'Matrícula',
      'Nombre',
      'Campus',
      'Grupo',
      'Periodo',
      'Cobranza',
      'Cnc',
      'Forma de pago',
      'Estatus',
      'Folio',
      'Observaciones',
      'Cajero',
      'Cargo',
      'Recargo',
      'Comisión 2%',
      'Importe',
    ];

    const detalleRows = filteredIngresos.map((row) => [
      row.no,
      row.fecha,
      row.matricula,
      row.nombre,
      row.campus || '',
      row.grupo,
      row.periodo,
      row.cobranza,
      row.cnc,
      row.formaPago,
      row.estado_pago || '',
      row.folio,
      row.observaciones || '',
      row.formaPago === 'PAGO_EN_LINEA' ? '' : (row.cajero || ''),
      Number(row.cargo.toFixed(2)),
      Number(row.recargo.toFixed(2)),
      row.formaPago === 'PAGO_EN_LINEA' ? Number(getComisionRow(row).toFixed(2)) : '',
      Number(getImporteConComisionRow(row).toFixed(2)),
    ]);

    sheetData.push(detalleHeader);
    detalleRows.forEach((r) => sheetData.push(r));

    const totalColumns = detalleHeader.length;
    const buildAlignedRow = () => Array(totalColumns).fill('');

    // Fila de totales alineada debajo de Cargo/Recargo/Comisión/Importe
    const totalesDetalleRow = buildAlignedRow();
    totalesDetalleRow[12] = 'Totales';
    totalesDetalleRow[14] = Number(totalCargo.toFixed(2));
    totalesDetalleRow[15] = Number(totalRecargo.toFixed(2));
    totalesDetalleRow[16] = Number(totalComision.toFixed(2));
    totalesDetalleRow[17] = Number(ingresoTotalDia.toFixed(2));
    sheetData.push(totalesDetalleRow);

    // Fila de ingreso total del día (resumen)
    sheetData.push([]);
    const ingresoTotalRow = buildAlignedRow();
    ingresoTotalRow[16] = 'INGRESO TOTAL DEL DÍA';
    ingresoTotalRow[17] = Number(ingresoTotalDia.toFixed(2));
    sheetData.push(ingresoTotalRow);

    // Totales por sección (alineados con columnas de Cargo/Recargo/Importe)
    sheetData.push([]);
    sheetData.push(['', 'TOTALES POR SECCIÓN']);

    const seccionHeader = buildAlignedRow();
    seccionHeader[1] = 'Sección';
    seccionHeader[14] = 'Cargo';
    seccionHeader[15] = 'Recargo';
    seccionHeader[16] = 'Comisión 2%';
    seccionHeader[17] = 'Importe';
    const seccionRows = totalesPorSeccion.map((row) => {
      const alignedRow = buildAlignedRow();
      alignedRow[1] = row.seccion;
      alignedRow[14] = Number(row.cargo.toFixed(2));
      alignedRow[15] = Number(row.recargo.toFixed(2));
      alignedRow[16] = Number((row.comision || 0).toFixed(2));
      alignedRow[17] = Number(row.total.toFixed(2));
      return alignedRow;
    });

    sheetData.push(seccionHeader);
    seccionRows.forEach((r) => sheetData.push(r));

    // Totales por serie (alineados con columnas de Cargo/Recargo/Importe)
    sheetData.push([]);
    sheetData.push(['', 'TOTALES POR SERIE']);

    const serieHeader = buildAlignedRow();
    serieHeader[1] = 'Serie';
    serieHeader[14] = 'Cargo';
    serieHeader[15] = 'Recargo';
    serieHeader[16] = 'Comisión 2%';
    serieHeader[17] = 'Importe';
    const serieRows = totalesPorSerie.map((row) => {
      const alignedRow = buildAlignedRow();
      alignedRow[1] = row.serie;
      alignedRow[14] = Number(row.cargo.toFixed(2));
      alignedRow[15] = Number(row.recargo.toFixed(2));
      alignedRow[16] = Number((row.comision || 0).toFixed(2));
      alignedRow[17] = Number(row.total.toFixed(2));
      return alignedRow;
    });


    sheetData.push(serieHeader);
    serieRows.forEach((r) => sheetData.push(r));

    // --- TOTALES POR CONCEPTO ---
    sheetData.push([]);
    sheetData.push(['', 'TOTALES POR CONCEPTO']);
    const conceptoHeader = buildAlignedRow();
    conceptoHeader[1] = 'Concepto';
    conceptoHeader[2] = 'Nombre concepto';
    conceptoHeader[14] = 'Cargo';
    conceptoHeader[15] = 'Recargo';
    conceptoHeader[16] = 'Comisión 2%';
    conceptoHeader[17] = 'Importe';
    const conceptoRows = totalesPorConcepto.map((row) => {
      const alignedRow = buildAlignedRow();
      alignedRow[1] = row.concepto;
      alignedRow[2] = row.nombre_concepto;
      alignedRow[14] = Number(row.cargo.toFixed(2));
      alignedRow[15] = Number(row.recargo.toFixed(2));
      alignedRow[16] = Number((row.comision || 0).toFixed(2));
      alignedRow[17] = Number(row.total.toFixed(2));
      return alignedRow;
    });
    sheetData.push(conceptoHeader);
    conceptoRows.forEach((r) => sheetData.push(r));

    const sheet = XLSX.utils.aoa_to_sheet(sheetData);

    // Estilos básicos para que el encabezado se vea centrado y en negritas
    sheet['!merges'] = [
      // Centro De Estudios... (fila 1, columnas A a M)
      { s: { r: 0, c: 0 }, e: { r: 0, c: 12 } },
      // Dirección (fila 2, columnas A a M)
      { s: { r: 1, c: 0 }, e: { r: 1, c: 12 } },
      // Reporte de ingresos (fila 3, columnas A a M)
      { s: { r: 2, c: 0 }, e: { r: 2, c: 12 } },
    ];

    const titleStyle = {
      font: { name: 'Calibri', sz: 14, bold: true },
      alignment: { horizontal: 'center' },
    };
    const subtitleStyle = {
      font: { name: 'Calibri', sz: 11, bold: true },
      alignment: { horizontal: 'center' },
    };

    if (sheet.A1) sheet.A1.s = titleStyle;
    if (sheet.A2) sheet.A2.s = subtitleStyle;
    if (sheet.A3) sheet.A3.s = subtitleStyle;

    // Encabezado de columnas del detalle
    const headerStyle = {
      font: { name: 'Calibri', sz: 10, bold: true },
      alignment: { horizontal: 'center' },
    };
    const headerCols = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R'];
    headerCols.forEach((col) => {
      const addr = `${col}5`;
      if (sheet[addr]) {
        sheet[addr].s = headerStyle;
      }
    });

    XLSX.utils.book_append_sheet(wb, sheet, 'Ingresos diarios');

    XLSX.writeFile(wb, 'reporte_ingresos_diarios.xlsx', { cellStyles: true });
  };
  
  // Estado para formato de exportación (por defecto Excel)
  const [exportFormat, setExportFormat] = useState('EXCEL');
  const reportRef = useRef(null);

  const handleDownloadPDF = async () => {
    // Generar PDF del contenedor del reporte usando el util
    try {
      const fechaRangoTexto = startDate || endDate
        ? startDate && endDate
          ? `${formatDisplayDate(startDate)} al ${formatDisplayDate(endDate)}`
          : formatDisplayDate(startDate || endDate)
        : 'Sin rango de fechas';

      if (!reportRef.current) {
        // fallback a imprimir si no hay contenedor
        window.print();
        return;
      }

      // Generar a partir de datos (usar todos los registros filtrados, no solo la página)
      await generateReporteIngresosPDF({
        rows: filteredIngresos.map((row) => ({
          ...row,
          comision: getComisionRow(row),
          importe: getImporteConComisionRow(row),
        })),
        totalesPorSeccion,
        totalesPorSerie,
        totalesPorConcepto,
        ingresoTotalDia,
        title: 'Reporte de ingresos',
        fechaRango: fechaRangoTexto,
        descargar: true,
      });
    } catch (err) {
      console.error('Error generando PDF del reporte:', err);
      // fallback
      window.print();
    }
  };

  const handlePrint = () => {
    if (exportFormat === 'EXCEL') {
      handleDownloadExcel();
    } else {
      handleDownloadPDF();
    }
  };

  return (
    <div className={layoutStyles.mainContainer} style={{ height: '100vh', display: 'flex' }}>
      <div className={`${layoutStyles.layout} ${isCollapsed ? layoutStyles.sidebarCollapsedLayout : ''}`} style={{ flex: 1, display: 'flex', height: '100vh' }}>
        <div className={layoutStyles.sidebarColumn} style={{ height: '100vh', minHeight: 0 }}>
          <Sidebar />
        </div>
        <div className={layoutStyles.contentColumn} style={{ position: 'relative', height: '100vh', overflowY: 'auto', minHeight: 0 }}>
          <LoaderOverlay visible={loading} label="Cargando reporte de ingresos..." />
          <HeaderGlobal
            title="Reporte de Ingresos Diarios"
            campus={selectedCampus}
            onCampusChange={e => {
              const campus = e.target?.value || '';
              setSelectedCampus(campus);
            }}
          />
          {/* ...el resto del contenido de contentColumn... */}
          <main className={layoutStyles.mainContent}>
            <div className={layoutStyles.contentArea}>
              {/* Scroll horizontal para la tabla principal y totales */}
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                  <div className={estudiantesStyles.searchRow}>
                    <div className={estudiantesStyles.formGroup}>
                      <label className={estudiantesStyles.searchLabel}>Rango de fechas</label>
                      <DatePicker
                        selectsRange
                        startDate={startDate}
                        endDate={endDate}
                        onChange={(update) => {
                          setDateRange(update);
                        }}
                        customInput={<DateRangeInput start={startDate} end={endDate} />}
                      />
                    </div>
                    {/* MultiSelect de conceptos (cnc) con checkboxes y opción Todos */}
                    <div className={estudiantesStyles.formGroup}>
                      <label className={estudiantesStyles.searchLabel}>Concepto</label>
                      <MultiSelect
                        options={conceptosUnicos}
                        value={selectedConceptos}
                        onChange={selected => {
                          setSelectedConceptos(selected);
                        }}
                        labelledBy="Selecciona conceptos"
                        hasSelectAll={false}
                        overrideStrings={{ selectSomeItems: "Selecciona conceptos", allItemsAreSelected: "Todos seleccionados" }}
                        disableSearch={true}
                        classNamePrefix="multi-select"
                        valueRenderer={(selected) =>
                          selected.length === 0
                            ? 'Selecciona conceptos'
                            : selected.length === 1
                            ? selected[0].label
                            : `${selected.length} conceptos seleccionados`
                        }
                      />
                    </div>
                    {/* Filtro de búsqueda */}
                    <div className={estudiantesStyles.formGroup}>
                      <label className={estudiantesStyles.searchLabel}>Folio, matrícula o nombre</label>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar por folio, concepto de cobro, matrícula, nombre, periodo o cobranza"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className={estudiantesStyles.formGroup}>
                      <label className={estudiantesStyles.searchLabel}>Cajero</label>
                      <MultiSelect
                        options={cajerosOptions}
                        value={selectedCajeros}
                        onChange={selected => setSelectedCajeros(selected)}
                        labelledBy="Selecciona cajeros"
                        hasSelectAll={false}
                        overrideStrings={{ selectSomeItems: "Selecciona cajeros", allItemsAreSelected: "Todos seleccionados" }}
                        disableSearch={true}
                        classNamePrefix="multi-select"
                        valueRenderer={(selected) =>
                          selected.length === 0
                            ? 'Selecciona cajeros'
                            : selected.length === 1
                            ? selected[0].label
                            : `${selected.length} cajeros seleccionados`
                        }
                      />
                    </div>
                    <div className={estudiantesStyles.formGroup}>
                      <label className={estudiantesStyles.searchLabel}>Forma de Pago</label>
                      <MultiSelect
                        options={formasPagoUnicas}
                        value={selectedFormasPago}
                        onChange={selected => setSelectedFormasPago(selected)}
                        labelledBy="Selecciona formas de pago"
                        hasSelectAll={false}
                        overrideStrings={{ selectSomeItems: "Selecciona forma de pago", allItemsAreSelected: "Todas seleccionadas" }}
                        disableSearch={true}
                        classNamePrefix="multi-select"
                        valueRenderer={(selected) =>
                          selected.length === 0
                            ? 'Selecciona forma de pago'
                            : selected.length === 1
                            ? selected[0].label
                            : `${selected.length} formas seleccionadas`
                        }
                      />
                    </div>
                    <div className={estudiantesStyles.formGroup}>
                      <label className={estudiantesStyles.searchLabel}>Estatus</label>
                      <select
                        className={estudiantesStyles.searchInput}
                        value={selectedStatus}
                        onChange={e => setSelectedStatus(e.target.value)}
                        style={{ textAlign: 'center' }}
                      >
                        <option value="APLICADO" style={{ textAlign: 'center' }}>Aplicados</option>
                        <option value="CANCELADO" style={{ textAlign: 'center' }}>Cancelados</option>
                        <option value="TODOS" style={{ textAlign: 'center' }}>Todos</option>
                      </select>
                    </div>
                    <div className={reportStyles.filterActions}>
                      <button
                        type="button"
                        className={reportStyles.clearFiltersButton}
                        onClick={() => {
                          setSearchTerm('');
                          setDateRange([null, null]);
                          setSelectedCajeros([]);
                          setSelectedFormasPago([]);
                          setSelectedStatus('APLICADO');
                          setSelectedConceptos([]);
                        }}
                      >
                        Limpiar filtros
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              </div>
              <div className={`${layoutStyles.contentWrapper} ${reportStyles.reportWrapper}`} ref={reportRef}>
                <div className={reportStyles.reportContainer}>
                  <div className={reportStyles.actionsBar}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value)}
                        className={estudiantesStyles.searchInput}
                        aria-label="Formato de exportación"
                        style={{ textAlign: 'center' }}
                      >
                        <option value="EXCEL" style={{ textAlign: 'center' }}>Excel</option>
                        <option value="PDF" style={{ textAlign: 'center' }}>PDF</option>
                      </select>
                      <button
                        type="button"
                        className={`${reportStyles.downloadButton} bg-blue-900 text-white border-transparent`}
                        onClick={handlePrint}
                        style={{ backgroundColor: '#1e3a8a', color: '#ffffff', borderColor: 'transparent' }}
                      >
                        <span className={reportStyles.downloadIcon}>
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12 3v12m0 0 4-4m-4 4-4-4"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M5 15v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span>Imprimir</span>
                      </button>
                    </div>
                  </div>
                  <header className={reportStyles.reportHeader}>
                    <div className={reportStyles.headerLeft}>
                      <div className={reportStyles.logoPlaceholder} />
                    </div>
                    <div className={reportStyles.headerCenter}>
                      <h2 className={reportStyles.institutionName}>Centro De Estudios Superiores CMP, A.C.</h2>
                      <div className={reportStyles.institutionAddress}>
                        Ave. Venustiano Carranza Nte. No.Ext. 614 CENTRO CP 64000 MONTERREY, NUEVO LEÓN
                      </div>
                      <div className={reportStyles.reportTitleRow}>
                        <span>Reporte de ingresos</span>
                        <span className={reportStyles.reportDate}>
                          {startDate || endDate
                            ? startDate && endDate
                              ? `${formatDisplayDate(startDate)} al ${formatDisplayDate(endDate)}`
                              : formatDisplayDate(startDate || endDate)
                            : 'Sin rango de fechas'}
                        </span>
                      </div>
                    </div>
                  </header>

                  <section className={reportStyles.tableSection}>
                    <div className={reportStyles.tableScrollWrapper}>
                      <table className={reportStyles.dataTable}>
                        <thead>
                          <tr>
                            <th onClick={() => handleSort('no')} style={{ cursor: 'pointer' }}>No.{renderSortIcon('no')}</th>
                            <th onClick={() => handleSort('fecha')} style={{ cursor: 'pointer' }}>Fecha{renderSortIcon('fecha')}</th>
                            <th onClick={() => handleSort('matricula')} style={{ cursor: 'pointer' }}>Matrícula{renderSortIcon('matricula')}</th>
                            <th onClick={() => handleSort('nombre')} style={{ cursor: 'pointer' }}>Nombre{renderSortIcon('nombre')}</th>
                            <th onClick={() => handleSort('campus')} style={{ cursor: 'pointer' }}>Campus{renderSortIcon('campus')}</th>
                            <th onClick={() => handleSort('grupo')} style={{ cursor: 'pointer' }}>Grupo{renderSortIcon('grupo')}</th>
                            <th onClick={() => handleSort('periodoCobranza')} style={{ cursor: 'pointer' }}>Periodo/Cobranza{renderSortIcon('periodoCobranza')}</th>
                            <th onClick={() => handleSort('cnc')} style={{ cursor: 'pointer' }}>Cnc{renderSortIcon('cnc')}</th>
                            <th onClick={() => handleSort('formaPago')} style={{ cursor: 'pointer' }}>Fm.Pag{renderSortIcon('formaPago')}</th>
                            <th onClick={() => handleSort('estado_pago')} style={{ cursor: 'pointer' }}>Estatus{renderSortIcon('estado_pago')}</th>
                            <th onClick={() => handleSort('folio')} style={{ cursor: 'pointer' }}>Folio{renderSortIcon('folio')}</th>
                            <th onClick={() => handleSort('observaciones')} style={{ cursor: 'pointer' }}>Observaciones{renderSortIcon('observaciones')}</th>
                            <th onClick={() => handleSort('cajero')} style={{ cursor: 'pointer' }}>Cajero{renderSortIcon('cajero')}</th>
                            <th className={reportStyles.numeric} onClick={() => handleSort('cargo')} style={{ cursor: 'pointer' }}>Cargo{renderSortIcon('cargo')}</th>
                            <th className={reportStyles.numeric} onClick={() => handleSort('recargo')} style={{ cursor: 'pointer' }}>Recargo{renderSortIcon('recargo')}</th>
                            <th className={reportStyles.numeric} onClick={() => handleSort('comision')} style={{ cursor: 'pointer' }}>Comisión 2%{renderSortIcon('comision')}</th>
                            <th className={reportStyles.numeric} onClick={() => handleSort('importe')} style={{ cursor: 'pointer' }}>Importe{renderSortIcon('importe')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedIngresos.map((row) => (
                            <tr key={row.no}>
                              <td className={reportStyles.center}>{row.no}</td>
                              <td className={reportStyles.center}>{row.fecha}</td>
                              <td className={reportStyles.center}>{row.matricula}</td>
                              <td>{row.nombre}</td>
                              <td className={reportStyles.center}>{row.campus || ''}</td>
                              <td className={reportStyles.center}>{row.grupo}</td>
                              <td className={reportStyles.center}>{`${row.periodo} / ${row.cobranza}`}</td>
                              <td className={reportStyles.center}>{row.cnc}</td>
                              <td className={reportStyles.center}>{row.formaPago}</td>
                              <td className={reportStyles.center}>{row.estado_pago || ''}</td>
                              <td className={reportStyles.center}>{row.folio}</td>
                              <td className={reportStyles.center}>{row.observaciones}</td>
                              <td className={reportStyles.center}>{row.formaPago === 'PAGO_EN_LINEA' ? '' : (row.cajero || '')}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.cargo)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.recargo)}</td>
                              <td className={reportStyles.numeric}>
                                {row.formaPago === 'PAGO_EN_LINEA' ? formatMoney(getComisionRow(row)) : '-'}
                              </td>
                              <td className={reportStyles.numeric}>{formatMoney(getImporteConComisionRow(row))}</td>
                            </tr>
                          ))}
                          {filteredIngresos.length > 0 && (
                            <tr>
                              <td colSpan={12} style={{ fontWeight: 700, textAlign: 'right' }}>Totales</td>
                              <td className={reportStyles.center}></td>
                              <td className={reportStyles.numeric} style={{ fontWeight: 700 }}>{formatMoney(totalCargo)}</td>
                              <td className={reportStyles.numeric} style={{ fontWeight: 700 }}>{formatMoney(totalRecargo)}</td>
                              <td className={reportStyles.numeric} style={{ fontWeight: 700 }}>{formatMoney(totalComision)}</td>
                              <td className={reportStyles.numeric} style={{ fontWeight: 700 }}>{formatMoney(ingresoTotalDia)}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                      {!loading && filteredIngresos.length === 0 && (
                        <div
                          style={{
                            marginTop: '8px',
                            textAlign: 'center',
                            fontSize: '0.9rem',
                            color: '#777',
                          }}
                        >
                          {startDate || endDate
                            ? 'No hay ingresos registrados para la fecha seleccionada.'
                            : 'Sin datos'}
                        </div>
                      )}
                  </section>

                  {filteredIngresos.length > pageSize && (
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'center' }}>
                      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
                    </div>
                  )}

                  <section className={reportStyles.summarySection}>
                    <div className={reportStyles.summaryRow}>
                      <span className={reportStyles.summaryLabel}>INGRESO TOTAL DEL DÍA</span>
                      <span className={reportStyles.summaryValue}>{formatMoney(ingresoTotalDia)}</span>
                    </div>
                  </section>

                  <section className={reportStyles.totalsSection} style={{ minWidth: 600 }}>
                    <div className={reportStyles.totalsBlock}>
                      <div className={reportStyles.totalsTitle}>TOTALES POR SECCIÓN</div>
                      <table className={reportStyles.totalsTable}>
                        <thead>
                          <tr>
                            <th>Sección</th>
                            <th className={reportStyles.numeric}>Cargo</th>
                            <th className={reportStyles.numeric}>Recargo</th>
                            <th className={reportStyles.numeric}>Comisión 2%</th>
                            <th className={reportStyles.numeric}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {totalesPorSeccion.map((row) => (
                            <tr key={row.seccion}>
                              <td>{row.seccion}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.cargo)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.recargo)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.comision)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className={reportStyles.totalsBlock}>
                      <div className={reportStyles.totalsTitle}>TOTALES POR SERIE</div>
                      <table className={reportStyles.totalsTable}>
                        <thead>
                          <tr>
                            <th>Serie</th>
                            <th className={reportStyles.numeric}>Cargo</th>
                            <th className={reportStyles.numeric}>Recargo</th>
                            <th className={reportStyles.numeric}>Comisión 2%</th>
                            <th className={reportStyles.numeric}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {totalesPorSerie.map((row) => (
                            <tr key={row.serie}>
                              <td>{row.serie}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.cargo)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.recargo)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.comision)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  {/* Tabla de totales por concepto (CNC) ocupando todo el ancho */}
                  <section style={{ marginTop: 16 }}>
                    <div className={reportStyles.totalsBlock} style={{ width: '100%', minWidth: 600 }}>
                      <div className={reportStyles.totalsTitle}>TOTALES POR CONCEPTO</div>
                      <table className={reportStyles.totalsTable}>
                        <thead>
                          <tr>
                            <th>Concepto</th>
                            <th>Nombre concepto</th>
                            <th className={reportStyles.numeric}>Cargo</th>
                            <th className={reportStyles.numeric}>Recargo</th>
                            <th className={reportStyles.numeric}>Comisión 2%</th>
                            <th className={reportStyles.numeric}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {totalesPorConcepto.map((row) => (
                            <tr key={row.concepto}>
                              <td>{row.concepto}</td>
                              <td>{row.nombre_concepto}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.cargo)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.recargo)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.comision)}</td>
                              <td className={reportStyles.numeric}>{formatMoney(row.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default ReportesIngresosDiariosScreen;
